import React, { useState } from 'react';
import QRCode from 'qrcode';
import { useAuth } from '../contexts/AuthContext';
import {
    authType,
    confirmSignIn,
    signInWithPasskey,
    initiatePasswordReset,
    completePasswordReset,
    getMFAPreference,
    setupTOTP,
    verifyTOTP,
    updatePhoneNumber,
    confirmPhoneNumber,
    setMFAPreference,
} from '../services/auth';
import type { SignInOutput } from '../services/auth';
import './Login.css';

type LoginStep =
    | 'login'
    | 'new_password'
    | 'mfa_setup_selection'
    | 'totp_setup'
    | 'totp_setup_post_login'
    | 'mfa_method_choice_post_login'
    | 'sms_setup_post_login'
    | 'sms_verify_post_login'
    | 'sms_setup'
    | 'mfa_code'
    | 'forgot_password'
    | 'forgot_password_confirm';

export const Login: React.FC = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [mfaCode, setMfaCode] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [resetCode, setResetCode] = useState('');
    const [resetEmail, setResetEmail] = useState('');
    const [resetNewPassword, setResetNewPassword] = useState('');
    const [resetConfirmPassword, setResetConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState<LoginStep>('login');
    const [qrCodeUrl, setQrCodeUrl] = useState('');
    const [totpSecret, setTotpSecret] = useState('');
    const [mfaType, setMfaType] = useState<'totp' | 'sms'>('totp');

    const { signIn, refreshUser } = useAuth();

    const handleSignInResult = async (result: SignInOutput) => {
        const nextStep = result.nextStep.signInStep;

        if (result.isSignedIn || nextStep === 'DONE') {
            try {
                const pref = await getMFAPreference();
                const hasMfa = pref.preferred || (pref.enabled && pref.enabled.length > 0);
                if (!hasMfa) {
                    setStep('mfa_method_choice_post_login');
                    return;
                }
            } catch { /* MFA check optional */ }
            await refreshUser();
            return;
        }

        switch (nextStep) {
            case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
                setStep('new_password');
                break;
            case 'CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION':
                setStep('mfa_setup_selection');
                break;
            case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP': {
                const details = result.nextStep.totpSetupDetails;
                if (details) {
                    const uri = details.getSetupUri('SCAIT', email).toString();
                    setTotpSecret(details.sharedSecret);
                    try {
                        const url = await QRCode.toDataURL(uri, { width: 200 });
                        setQrCodeUrl(url);
                    } catch {
                        setQrCodeUrl('');
                    }
                }
                setStep('totp_setup');
                break;
            }
            case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
                setMfaType('totp');
                setStep('mfa_code');
                break;
            case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
                setMfaType('sms');
                setStep('mfa_code');
                break;
            default:
                setError(`Unexpected authentication step: ${nextStep}`);
                break;
        }
    };

    const handleAzureLogin = () => {
        setLoading(true);
        signIn('', '');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const result = await signIn(email, password);
            if (result) {
                await handleSignInResult(result as SignInOutput);
            }
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to sign in');
        } finally {
            setLoading(false);
        }
    };

    const handlePasskeyLogin = async () => {
        if (!email) {
            setError('Please enter your email address first');
            return;
        }
        setError('');
        setLoading(true);

        try {
            const result = await signInWithPasskey(email);
            await handleSignInResult(result);
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Passkey sign-in failed');
        } finally {
            setLoading(false);
        }
    };

    const handleNewPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        const validationError = validatePassword(newPassword, confirmPassword);
        if (validationError) {
            setError(validationError);
            return;
        }

        setLoading(true);

        try {
            const result = await confirmSignIn(newPassword);
            await handleSignInResult(result);
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to change password');
        } finally {
            setLoading(false);
        }
    };

    const handleMfaSetupSelection = async (method: 'TOTP' | 'SMS') => {
        setError('');
        setLoading(true);

        try {
            const result = await confirmSignIn(method);
            if (method === 'SMS' && !result.isSignedIn) {
                setStep('sms_setup');
                setLoading(false);
                return;
            }
            await handleSignInResult(result);
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to select MFA method');
        } finally {
            setLoading(false);
        }
    };

    const handleSmsSetup = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        const digits = phoneNumber.replace(/\D/g, '');
        const normalized = phoneNumber.startsWith('+') ? phoneNumber : `+1${digits}`;

        if (!/^\+1\d{10}$/.test(normalized)) {
            setError('Enter a valid 10-digit US phone number (e.g., 212-555-1234)');
            return;
        }
        setPhoneNumber(normalized);

        setLoading(true);

        try {
            const result = await confirmSignIn(normalized);
            await handleSignInResult(result);
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to set up SMS');
        } finally {
            setLoading(false);
        }
    };

    const handleTotpVerify = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const result = await confirmSignIn(mfaCode);
            await handleSignInResult(result);
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Invalid verification code');
        } finally {
            setLoading(false);
            setMfaCode('');
        }
    };

    const handleMfaCode = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const result = await confirmSignIn(mfaCode);
            await handleSignInResult(result);
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Invalid verification code';
            if (msg.toLowerCase().includes('already been used')) {
                setError('This code has already been used. Wait for your authenticator app to generate a new code and try again.');
            } else {
                setError(msg);
            }
        } finally {
            setLoading(false);
            setMfaCode('');
        }
    };

    const handlePostLoginTotpVerify = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await verifyTOTP(mfaCode);
            await refreshUser();
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Invalid verification code');
        } finally {
            setLoading(false);
            setMfaCode('');
        }
    };

    const handlePostLoginChooseTotp = async () => {
        setError('');
        setLoading(true);
        try {
            const totpResult = await setupTOTP();
            const uri = totpResult.getSetupUri('SCAIT', email).toString();
            setTotpSecret(totpResult.sharedSecret);
            try {
                const url = await QRCode.toDataURL(uri, { width: 200 });
                setQrCodeUrl(url);
            } catch {
                setQrCodeUrl('');
            }
            setStep('totp_setup_post_login');
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to start TOTP setup');
        } finally {
            setLoading(false);
        }
    };

    const handlePostLoginChooseSms = () => {
        setError('');
        setPhoneNumber('');
        setStep('sms_setup_post_login');
    };

    const handlePostLoginSmsSubmitPhone = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        const digits = phoneNumber.replace(/\D/g, '');
        const normalized = phoneNumber.startsWith('+') ? phoneNumber : `+1${digits}`;

        if (!/^\+1\d{10}$/.test(normalized)) {
            setError('Enter a valid 10-digit US phone number (e.g., 212-555-1234)');
            return;
        }

        setPhoneNumber(normalized);
        setLoading(true);
        try {
            await updatePhoneNumber(normalized);
            setStep('sms_verify_post_login');
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to update phone number');
        } finally {
            setLoading(false);
        }
    };

    const handlePostLoginSmsVerify = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            await confirmPhoneNumber(mfaCode);
            await setMFAPreference('sms');
            await refreshUser();
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Invalid verification code');
        } finally {
            setLoading(false);
            setMfaCode('');
        }
    };

    const handleForgotPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await initiatePasswordReset(resetEmail);
            setStep('forgot_password_confirm');
            setSuccessMessage('A verification code has been sent to your email.');
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to send reset code');
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPasswordConfirm = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        const validationError = validatePassword(resetNewPassword, resetConfirmPassword);
        if (validationError) {
            setError(validationError);
            return;
        }

        setLoading(true);

        try {
            await completePasswordReset(resetEmail, resetCode, resetNewPassword);
            setSuccessMessage('Password reset successfully. Please sign in.');
            setStep('login');
            setResetEmail('');
            setResetCode('');
            setResetNewPassword('');
            setResetConfirmPassword('');
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to reset password');
        } finally {
            setLoading(false);
        }
    };

    const backToLogin = () => {
        setStep('login');
        setError('');
        setSuccessMessage('');
        setMfaCode('');
        setNewPassword('');
        setConfirmPassword('');
        setPhoneNumber('');
        setResetEmail('');
        setResetCode('');
        setResetNewPassword('');
        setResetConfirmPassword('');
        setQrCodeUrl('');
        setTotpSecret('');
    };

    if (authType === 'azure') {
        return (
            <div className="login-wrapper">
                <div className="login-card">
                    <div className="login-header">
                        <h2>Sign In</h2>
                        <p>Welcome to Supply Chain AI.<br />Please sign in with your corporate account.</p>
                    </div>
                    <button onClick={handleAzureLogin} disabled={loading} className="microsoft-button">
                        {loading ? (
                            <span className="spinning">&#x27F3;</span>
                        ) : (
                            <svg className="ms-logo" viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg">
                                <path fill="#f35325" d="M1 1h10v10H1z" />
                                <path fill="#81bc06" d="M12 1h10v10H12z" />
                                <path fill="#05a6f0" d="M1 12h10v10H1z" />
                                <path fill="#ffba08" d="M12 12h10v10H12z" />
                            </svg>
                        )}
                        <span>{loading ? 'Redirecting...' : 'Sign in with Microsoft'}</span>
                    </button>
                    <div className="login-footer">
                        <span className="copyright">&copy; 2026 Mitek Analytics Inc</span>
                        <span className="version">v1.1.1 &bull; Azure Secure Access</span>
                    </div>
                </div>
            </div>
        );
    }

    if (step === 'new_password') {
        return (
            <div className="login-wrapper">
                <div className="login-card">
                    <div className="login-header">
                        <h2>Change Password</h2>
                        <p>This is your first login. Please set a new password.</p>
                    </div>
                    <form onSubmit={handleNewPassword}>
                        <div className="form-group">
                            <label htmlFor="newPassword">New Password</label>
                            <input id="newPassword" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required disabled={loading} placeholder="At least 8 characters" />
                        </div>
                        <div className="form-group">
                            <label htmlFor="confirmPassword">Confirm Password</label>
                            <input id="confirmPassword" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required disabled={loading} />
                        </div>
                        {error && <div className="error-message">{error}</div>}
                        <PasswordRequirements />
                        <button type="submit" disabled={loading} className="login-btn-primary" style={{ width: '100%', marginTop: '10px' }}>
                            {loading ? 'Changing password...' : 'Change Password'}
                        </button>
                    </form>
                    <div className="login-footer"><span className="copyright">&copy; 2026 Mitek Analytics Inc</span></div>
                </div>
            </div>
        );
    }

    if (step === 'mfa_setup_selection') {
        return (
            <div className="login-wrapper">
                <div className="login-card">
                    <div className="login-header">
                        <h2>Set Up Two-Factor Authentication</h2>
                        <p>Choose your preferred verification method.</p>
                    </div>
                    {error && <div className="error-message">{error}</div>}
                    <div className="mfa-selection">
                        <button onClick={() => handleMfaSetupSelection('TOTP')} disabled={loading} className="mfa-option-button">
                            <span className="mfa-option-icon">&#128272;</span>
                            <span className="mfa-option-label">Authenticator App</span>
                            <span className="mfa-option-desc">Use Google Authenticator, 1Password, or similar</span>
                        </button>
                        <button onClick={() => handleMfaSetupSelection('SMS')} disabled={loading} className="mfa-option-button">
                            <span className="mfa-option-icon">&#128241;</span>
                            <span className="mfa-option-label">Text Message (SMS)</span>
                            <span className="mfa-option-desc">Receive a code via text message</span>
                        </button>
                    </div>
                    <div className="login-footer"><span className="copyright">&copy; 2026 Mitek Analytics Inc</span></div>
                </div>
            </div>
        );
    }

    if (step === 'totp_setup') {
        return (
            <div className="login-wrapper">
                <div className="login-card">
                    <div className="login-header">
                        <h2>Set Up Authenticator App</h2>
                        <p>Scan the QR code with your authenticator app, then enter the 6-digit code.</p>
                    </div>
                    {qrCodeUrl ? (
                        <div className="qr-container"><img src={qrCodeUrl} alt="TOTP QR Code" className="qr-code" /></div>
                    ) : totpSecret ? (
                        <div className="error-message">QR code generation failed. Use the manual entry key below.</div>
                    ) : null}
                    {totpSecret && (
                        <div className="secret-key">
                            <span className="secret-label">Manual entry key:</span>
                            <code className="secret-value">{totpSecret}</code>
                        </div>
                    )}
                    <form onSubmit={handleTotpVerify}>
                        <div className="form-group">
                            <label htmlFor="totpCode">Verification Code</label>
                            <input id="totpCode" type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))} required disabled={loading} placeholder="000000" autoComplete="one-time-code" />
                        </div>
                        {error && <div className="error-message">{error}</div>}
                        <button type="submit" disabled={loading || mfaCode.length !== 6} className="login-btn-primary" style={{ width: '100%', marginTop: '10px' }}>
                            {loading ? 'Verifying...' : 'Verify & Activate'}
                        </button>
                    </form>
                    <div className="login-footer"><span className="copyright">&copy; 2026 Mitek Analytics Inc</span></div>
                </div>
            </div>
        );
    }

    if (step === 'totp_setup_post_login') {
        return (
            <div className="login-wrapper">
                <div className="login-card">
                    <div className="login-header">
                        <h2>Set Up Two-Factor Authentication</h2>
                        <p>Your account requires two-factor authentication. Scan the QR code with your authenticator app, then enter the 6-digit code.</p>
                    </div>
                    {qrCodeUrl ? (
                        <div className="qr-container"><img src={qrCodeUrl} alt="TOTP QR Code" className="qr-code" /></div>
                    ) : totpSecret ? (
                        <div className="error-message">QR code generation failed. Use the manual entry key below.</div>
                    ) : null}
                    {totpSecret && (
                        <div className="secret-key">
                            <span className="secret-label">Manual entry key:</span>
                            <code className="secret-value">{totpSecret}</code>
                        </div>
                    )}
                    <form onSubmit={handlePostLoginTotpVerify}>
                        <div className="form-group">
                            <label htmlFor="totpCode">Verification Code</label>
                            <input id="totpCode" type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))} required disabled={loading} placeholder="000000" autoComplete="one-time-code" />
                        </div>
                        {error && <div className="error-message">{error}</div>}
                        <button type="submit" disabled={loading || mfaCode.length !== 6} className="login-btn-primary" style={{ width: '100%', marginTop: '10px' }}>
                            {loading ? 'Verifying...' : 'Verify & Activate'}
                        </button>
                    </form>
                    <div className="login-footer"><span className="copyright">&copy; 2026 Mitek Analytics Inc</span></div>
                </div>
            </div>
        );
    }

    if (step === 'mfa_method_choice_post_login') {
        return (
            <div className="login-wrapper">
                <div className="login-card">
                    <div className="login-header">
                        <h2>Set Up Two-Factor Authentication</h2>
                        <p>Your account requires two-factor authentication. Choose your preferred method.</p>
                    </div>
                    {error && <div className="error-message">{error}</div>}
                    <div className="mfa-selection">
                        <button onClick={handlePostLoginChooseTotp} disabled={loading} className="mfa-option-button">
                            <span className="mfa-option-icon">&#128272;</span>
                            <span className="mfa-option-label">Authenticator App</span>
                            <span className="mfa-option-desc">Use Google Authenticator, 1Password, or similar</span>
                        </button>
                        <button onClick={handlePostLoginChooseSms} disabled={loading} className="mfa-option-button">
                            <span className="mfa-option-icon">&#128241;</span>
                            <span className="mfa-option-label">Text Message (SMS)</span>
                            <span className="mfa-option-desc">Receive a code via text message</span>
                        </button>
                    </div>
                    <div className="login-footer"><span className="copyright">&copy; 2026 Mitek Analytics Inc</span></div>
                </div>
            </div>
        );
    }

    if (step === 'sms_setup_post_login') {
        return (
            <div className="login-wrapper">
                <div className="login-card">
                    <div className="login-header">
                        <h2>Set Up SMS Verification</h2>
                        <p>Enter your phone number to receive verification codes via text message.</p>
                    </div>
                    <form onSubmit={handlePostLoginSmsSubmitPhone}>
                        <div className="form-group">
                            <label htmlFor="phoneNumber">Phone Number</label>
                            <input id="phoneNumber" type="tel" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} required disabled={loading} placeholder="(212) 555-1234" />
                        </div>
                        {error && <div className="error-message">{error}</div>}
                        <button type="submit" disabled={loading} className="login-btn-primary" style={{ width: '100%', marginTop: '10px' }}>
                            {loading ? 'Sending code...' : 'Send Verification Code'}
                        </button>
                    </form>
                    <button onClick={() => setStep('mfa_method_choice_post_login')} className="back-link">Back to method selection</button>
                    <div className="login-footer"><span className="copyright">&copy; 2026 Mitek Analytics Inc</span></div>
                </div>
            </div>
        );
    }

    if (step === 'sms_verify_post_login') {
        return (
            <div className="login-wrapper">
                <div className="login-card">
                    <div className="login-header">
                        <h2>Verify Phone Number</h2>
                        <p>Enter the 6-digit code sent to {phoneNumber}.</p>
                    </div>
                    <form onSubmit={handlePostLoginSmsVerify}>
                        <div className="form-group">
                            <label htmlFor="smsCode">Verification Code</label>
                            <input id="smsCode" type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))} required disabled={loading} placeholder="000000" autoComplete="one-time-code" />
                        </div>
                        {error && <div className="error-message">{error}</div>}
                        <button type="submit" disabled={loading || mfaCode.length !== 6} className="login-btn-primary" style={{ width: '100%', marginTop: '10px' }}>
                            {loading ? 'Verifying...' : 'Verify & Activate'}
                        </button>
                    </form>
                    <div className="login-footer"><span className="copyright">&copy; 2026 Mitek Analytics Inc</span></div>
                </div>
            </div>
        );
    }

    if (step === 'sms_setup') {
        return (
            <div className="login-wrapper">
                <div className="login-card">
                    <div className="login-header">
                        <h2>Set Up SMS Verification</h2>
                        <p>Enter your phone number to receive verification codes via text message.</p>
                    </div>
                    <form onSubmit={handleSmsSetup}>
                        <div className="form-group">
                            <label htmlFor="phoneNumber">Phone Number</label>
                            <input id="phoneNumber" type="tel" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} required disabled={loading} placeholder="(212) 555-1234" />
                        </div>
                        {error && <div className="error-message">{error}</div>}
                        <button type="submit" disabled={loading} className="login-btn-primary" style={{ width: '100%', marginTop: '10px' }}>
                            {loading ? 'Sending code...' : 'Send Verification Code'}
                        </button>
                    </form>
                    <div className="login-footer"><span className="copyright">&copy; 2026 Mitek Analytics Inc</span></div>
                </div>
            </div>
        );
    }

    if (step === 'mfa_code') {
        return (
            <div className="login-wrapper">
                <div className="login-card">
                    <div className="login-header">
                        <h2>Two-Factor Authentication</h2>
                        <p>{mfaType === 'totp' ? 'Enter the 6-digit code from your authenticator app.' : 'Enter the 6-digit code sent to your phone.'}</p>
                    </div>
                    <form onSubmit={handleMfaCode}>
                        <div className="form-group">
                            <label htmlFor="mfaCode">Verification Code</label>
                            <input id="mfaCode" type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))} required disabled={loading} placeholder="000000" autoComplete="one-time-code" />
                        </div>
                        {error && <div className="error-message">{error}</div>}
                        <button type="submit" disabled={loading || mfaCode.length !== 6} className="login-btn-primary" style={{ width: '100%', marginTop: '10px' }}>
                            {loading ? 'Verifying...' : 'Verify'}
                        </button>
                    </form>
                    <div className="login-footer"><span className="copyright">&copy; 2026 Mitek Analytics Inc</span></div>
                </div>
            </div>
        );
    }

    if (step === 'forgot_password') {
        return (
            <div className="login-wrapper">
                <div className="login-card">
                    <div className="login-header">
                        <h2>Reset Password</h2>
                        <p>Enter your email address to receive a verification code.</p>
                    </div>
                    <form onSubmit={handleForgotPassword}>
                        <div className="form-group">
                            <label htmlFor="resetEmail">Email Address</label>
                            <input id="resetEmail" type="email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} required disabled={loading} placeholder="name@company.com" />
                        </div>
                        {error && <div className="error-message">{error}</div>}
                        <button type="submit" disabled={loading} className="login-btn-primary" style={{ width: '100%', marginTop: '10px' }}>
                            {loading ? 'Sending code...' : 'Send Reset Code'}
                        </button>
                    </form>
                    <button onClick={backToLogin} className="back-link">Back to Sign In</button>
                    <div className="login-footer"><span className="copyright">&copy; 2026 Mitek Analytics Inc</span></div>
                </div>
            </div>
        );
    }

    if (step === 'forgot_password_confirm') {
        return (
            <div className="login-wrapper">
                <div className="login-card">
                    <div className="login-header">
                        <h2>Reset Password</h2>
                        <p>Enter the code sent to your email and your new password.</p>
                    </div>
                    {successMessage && <div className="success-message">{successMessage}</div>}
                    <form onSubmit={handleForgotPasswordConfirm}>
                        <div className="form-group">
                            <label htmlFor="resetCode">Verification Code</label>
                            <input id="resetCode" type="text" inputMode="numeric" value={resetCode} onChange={(e) => setResetCode(e.target.value)} required disabled={loading} placeholder="Enter code from email" />
                        </div>
                        <div className="form-group">
                            <label htmlFor="resetNewPassword">New Password</label>
                            <input id="resetNewPassword" type="password" value={resetNewPassword} onChange={(e) => setResetNewPassword(e.target.value)} required disabled={loading} placeholder="At least 8 characters" />
                        </div>
                        <div className="form-group">
                            <label htmlFor="resetConfirmPassword">Confirm New Password</label>
                            <input id="resetConfirmPassword" type="password" value={resetConfirmPassword} onChange={(e) => setResetConfirmPassword(e.target.value)} required disabled={loading} />
                        </div>
                        {error && <div className="error-message">{error}</div>}
                        <PasswordRequirements />
                        <button type="submit" disabled={loading} className="login-btn-primary" style={{ width: '100%', marginTop: '10px' }}>
                            {loading ? 'Resetting...' : 'Reset Password'}
                        </button>
                    </form>
                    <button onClick={backToLogin} className="back-link">Back to Sign In</button>
                    <div className="login-footer"><span className="copyright">&copy; 2026 Mitek Analytics Inc</span></div>
                </div>
            </div>
        );
    }

    return (
        <div className="login-wrapper">
            <div className="login-card">
                <div className="login-header">
                    <h2>Sign In</h2>
                    <p>Enter your credentials to continue.</p>
                </div>
                {successMessage && <div className="success-message">{successMessage}</div>}
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="email">Email Address</label>
                        <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={loading} placeholder="name@company.com" />
                    </div>
                    <div className="form-group">
                        <label htmlFor="password">Password</label>
                        <input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required disabled={loading} />
                    </div>
                    {error && <div className="error-message">{error}</div>}
                    <button type="submit" disabled={loading} className="login-btn-primary" style={{ width: '100%', marginTop: '10px' }}>
                        {loading ? 'Signing in...' : 'Sign In'}
                    </button>
                </form>

                <div className="login-divider"><span>or</span></div>

                <button onClick={handlePasskeyLogin} disabled={loading} className="passkey-button">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 18v3c0 .6.4 1 1 1h4v-3h3v-3h2l1.4-1.4a6.5 6.5 0 1 0-4-4Z" />
                        <circle cx="16.5" cy="7.5" r=".5" fill="currentColor" />
                    </svg>
                    <span>Sign in with Passkey</span>
                </button>

                <button onClick={() => { setStep('forgot_password'); setError(''); setSuccessMessage(''); setResetEmail(email); }} className="forgot-password-link">
                    Forgot Password?
                </button>

                <div className="login-footer">
                    <span className="copyright">&copy; 2026 Mitek Analytics Inc</span>
                    <span className="version">v1.1.1 &bull; Secure Access</span>
                </div>
            </div>
        </div>
    );
};

function validatePassword(password: string, confirm?: string): string | null {
    if (password.length < 8) return 'Password must be at least 8 characters long';
    if (password.length > 128) return 'Password must be no more than 128 characters';
    if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
    if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter';
    if (!/[0-9]/.test(password)) return 'Password must contain at least one number';
    if (!/[^A-Za-z0-9]/.test(password)) return 'Password must contain at least one special character';
    if (confirm !== undefined && password !== confirm) return 'Passwords do not match';
    return null;
}

const PasswordRequirements: React.FC = () => (
    <div className="password-requirements">
        Password must contain:
        <ul>
            <li>At least 8 characters</li>
            <li>Uppercase and lowercase letters</li>
            <li>At least one number</li>
            <li>At least one special character</li>
        </ul>
    </div>
);
